import { CHAT_PLACEHOLDER_TEXT, MAX_MESSAGE_LENGTH } from "@/constants/search-constants";
import { DISCLAIMER_TEXT } from "@/constants/disclaimer";
import { resolveSuggestedQuestions } from "@/utils/resolve-suggested-questions";
import { resolvePlaceholder } from "@/utils/resolve-placeholder";
import { WidgetRuntimeConfigProvider } from "@/context/widget-runtime-config";
import { env } from "@/env";
import { useChatManager } from "@/hooks/use-chat-manager";
import { CHAT_MODE } from "@/store/chat-state";
import {
  applyThemeVariables,
  applyThemeWithOverrides,
  buildAttributeOverrides,
  fetchWidgetTheme,
  injectBaseStyles,
  readWidgetAttributes,
} from "@/utils/widget-runtime";
import createCache from "@emotion/cache";
import { CacheProvider } from "@emotion/react";
import styled from "@emotion/styled";
import type { FC } from "react";
import { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom/client";
import { Sentry } from "../../instrument";
import { ChatContent } from "../chat-components/chat-content";
import { SourcesModal } from "../chat-components/sources-modal";
import { colors, radius, spacing } from "../design-tokens";

const SearchResultsContainer = styled.div`
  padding: ${spacing["3xl"]}px;
  background-color: ${colors.surface};
  border-radius: ${radius.xl}px;
`;

const SearchResultsComponent: FC = () => {
  const container = useRef<HTMLDivElement | null>(null);
  const chatManager = useChatManager();
  const [isSourcesOpen, setIsSourcesOpen] = useState(false);
  const [hasSubmittedQuery, setHasSubmittedQuery] = useState(false);

  // Parse and submit query from URL on mount
  useEffect(() => {
    if (!hasSubmittedQuery) {
      // Try query string first (?globalsearch_q=...), then fall back to hash (#globalsearch_q=...)
      // for backwards compatibility with existing implementations
      const searchParams = new URLSearchParams(window.location.search);
      let query = searchParams.get("globalsearch_q");

      if (!query) {
        // Fall back to hash-based URL parameters
        const hash = window.location.hash.substring(1);
        const hashParams = new URLSearchParams(hash);
        query = hashParams.get("globalsearch_q");
      }

      if (query) {
        // Validate query length before processing
        // Note: URLSearchParams.get() already decodes percent-encoded values,
        // so we don't need to call decodeURIComponent() again
        if (query.length > MAX_MESSAGE_LENGTH) {
          console.warn("Query parameter exceeds maximum allowed length");
          return;
        }

        const trimmedQuery = query.trim();

        // Validate after trimming
        if (!trimmedQuery) {
          console.warn("Invalid or empty query parameter");
          return;
        }

        // TODO: XSS vulnerability - basic HTML tag removal doesn't prevent javascript: URLs, data: URIs, or encoded payloads. Use DOMPurify or comprehensive validation
        // Sanitize by removing any potential script tags or HTML
        const sanitizedQuery = trimmedQuery.replace(/<[^>]*>/g, "");

        // Submit the sanitized query using the chat manager
        chatManager.setUserQuery(sanitizedQuery);
        chatManager.setMode(CHAT_MODE.INPUT);

        // Mark as submitted to prevent re-submission
        setHasSubmittedQuery(true);
      }
    }
  }, [chatManager, hasSubmittedQuery]);

  return (
    <>
      <SearchResultsContainer ref={container}>
        <ChatContent {...chatManager} isSearchPage onSourcesClick={() => setIsSourcesOpen(true)} />
      </SearchResultsContainer>
      <SourcesModal
        isOpen={isSourcesOpen}
        onOpenChange={setIsSourcesOpen}
        sources={chatManager.response?.sources}
        container={container.current}
      />
    </>
  );
};

class SearchResultsWebComponent extends HTMLElement {
  private root: ReactDOM.Root | null = null;
  private isMounted = false;
  private abortController: AbortController | null = null;

  constructor() {
    super();
    this.attachShadow({ mode: "open" });
  }

  connectedCallback() {
    this.isMounted = true;
    this.abortController?.abort();
    this.abortController = new AbortController();
    // Inject base styles including system fonts
    injectBaseStyles(this);

    const attributes = readWidgetAttributes(this);
    if (!attributes.apiKey) {
      console.error("Tenant UI Widget: api-key attribute is required.");
      return;
    }

    const apiUrl = attributes.apiUrl ?? env.API_URL;
    const runtimeConfig = {
      apiKey: attributes.apiKey,
      apiUrl,
      organizationSlug: undefined as string | undefined,
      widgetDisclaimerText: DISCLAIMER_TEXT,
      widgetSuggestedQuestions: resolveSuggestedQuestions(
        attributes.suggestedQuestions,
        undefined,
      ),
      showChatQuestions: true,
      widgetPlaceholderText: resolvePlaceholder(attributes.placeholderText, undefined, CHAT_PLACEHOLDER_TEXT),
      langfuse: null as { baseUrl: string; publicKey: string; environment?: string } | null,
    };

    const colorOverrides = buildAttributeOverrides(attributes);

    applyThemeVariables(this, colorOverrides, true);

    // Create a custom Emotion cache for this shadow root
    const cache = createCache({
      key: "shadow-emotion-search",
      container: this.shadowRoot as unknown as Element,
    });

    void fetchWidgetTheme(apiUrl, attributes.apiKey, this.abortController.signal).then(({ theme, disclaimerText, suggestedQuestions, showChatQuestions, placeholderText, organizationSlug, langfuse }) => {
      if (!this.isMounted) {
        return;
      }
      applyThemeWithOverrides(this, theme, colorOverrides);
      runtimeConfig.organizationSlug = organizationSlug ?? runtimeConfig.organizationSlug;
      runtimeConfig.widgetDisclaimerText = disclaimerText ?? runtimeConfig.widgetDisclaimerText;
      runtimeConfig.widgetSuggestedQuestions = resolveSuggestedQuestions(
        attributes.suggestedQuestions,
        suggestedQuestions,
      );
      runtimeConfig.showChatQuestions = showChatQuestions;
      runtimeConfig.widgetPlaceholderText = resolvePlaceholder(
        attributes.placeholderText,
        placeholderText,
        CHAT_PLACEHOLDER_TEXT,
      );
      runtimeConfig.langfuse = langfuse ?? null;
      if (this.root) {
        this.root.render(
          <CacheProvider value={cache}>
            <WidgetRuntimeConfigProvider value={runtimeConfig}>
              <Sentry.ErrorBoundary
                beforeCapture={(scope) => {
                  scope.setTag("component", "tenant-ai-widget-search-results");
                  scope.setContext("widget", {
                    shadowDOM: true,
                    component: "SearchResultsWebComponent",
                  });
                }}
              >
                <SearchResultsComponent />
              </Sentry.ErrorBoundary>
            </WidgetRuntimeConfigProvider>
          </CacheProvider>,
        );
      }
    });

    this.root = ReactDOM.createRoot(this.shadowRoot as ShadowRoot);
    this.root.render(
      <CacheProvider value={cache}>
        <WidgetRuntimeConfigProvider value={runtimeConfig}>
          <Sentry.ErrorBoundary
            beforeCapture={(scope) => {
              scope.setTag("component", "tenant-ai-widget-search-results");
              scope.setContext("widget", {
                shadowDOM: true,
                component: "SearchResultsWebComponent",
              });
            }}
          >
            <SearchResultsComponent />
          </Sentry.ErrorBoundary>
        </WidgetRuntimeConfigProvider>
      </CacheProvider>,
    );
  }

  disconnectedCallback() {
    this.isMounted = false;
    this.abortController?.abort();
    this.abortController = null;
    if (this.root) {
      this.root.unmount();
      this.root = null;
    }
  }
}

export default SearchResultsWebComponent;
